/**
 * Tap-to-count tile entry, replacing typed notation for manual round entry.
 *
 * A tile has exactly 4 physical copies. Concealed counts (this widget's main
 * grid) and kongs share that budget — `usedByClaims` plus a tile's own
 * concealed count can never exceed 4, so a kong locks its tile out of the
 * grid instead of letting the same copies be counted twice.
 *
 * The pair (eye) is never a claim — mahjong has no such call — so it's just
 * whichever tile the player taps to a count of 2 in the grid; the scoring
 * engine finds it automatically when it tries every valid decomposition.
 *
 * Adding a kong never refuses for "not enough copies": it takes over any tiles of that face already in the hand
 * and adds however many are missing so the kong has four (see `kongPlan`).
 *
 * Kongs are the only meld this widget ever declares. A claimed pung or chow
 * doesn't need its tiles tracked separately at all — the scorer's decomposer
 * rediscovers a pung/chow from a flat tile pool on its own (see `decompose`
 * in shared/decompose.ts), so those tiles just live in the concealed grid like any
 * other. All that's lost by not tracking them individually is whether the
 * hand still counts as fully concealed, which is exactly what the "claimed a
 * pung or chow" checkbox below is for — see `HandInput.claimedSet`.
 */

import { esc } from './helpers';
import { kongNote, kongPlan } from './kongPlan';
import { bonusWindOf, WIND_TILE } from '../rules/tiles';
import type { BonusTile, Suit, Tile, Wind } from '../rules/tiles';
import type { Meld } from '../rules/types';

const NUMBERED_SUITS: { suit: Suit; label: string }[] = [
  { suit: 'm', label: 'Characters' },
  { suit: 'p', label: 'Dots' },
  { suit: 's', label: 'Bamboo' },
];

const HONOUR_TILES: Tile[] = ['1z', '2z', '3z', '4z', '5z', '6z', '7z'];
const HONOUR_LABEL: Record<Tile, string> = {
  '1z': 'East',
  '2z': 'South',
  '3z': 'West',
  '4z': 'North',
  '5z': 'White',
  '6z': 'Green',
  '7z': 'Red',
};

const BONUS_TILES: BonusTile[] = ['F1', 'F2', 'F3', 'F4', 'S1', 'S2', 'S3', 'S4'];

/** Picture-based tile faces, normalized separately from the vision-recognition reference set. */
function iconUrl(id: string): string {
  return `/tile-icons/${id}.png`;
}

const ALL_TILES: Tile[] = [
  ...NUMBERED_SUITS.flatMap(({ suit }) => Array.from({ length: 9 }, (_, i) => `${i + 1}${suit}`)),
  ...HONOUR_TILES,
];

interface DeclaredKong {
  tiles: Tile[];
  concealed: boolean;
  label: string;
  originLabel: string;
}

export interface TileGridPrefill {
  concealed: Tile[];
  melds: Meld[];
  winningTile?: Tile;
  flowers?: BonusTile[];
  claimedSet?: boolean;
}

/**
 * Which wind-pung / flower-bonus badges to show on the honour and bonus
 * tiles, and whether each is even worth showing — a badge for a toggle the
 * venue has switched off would just be misleading, not a helpful hint.
 */
export interface TileGridContext {
  seatWind: Wind;
  prevailingWind: Wind;
  showSeatWindBadge: boolean;
  showPrevailingWindBadge: boolean;
  showSeatFlowerBadge: boolean;
}

export interface TileGridResult {
  concealed: Tile[];
  melds: Meld[];
  winningTile: Tile | undefined;
  flowers: BonusTile[];
  claimedSet: boolean;
}

export interface TileGridHandle {
  read(): TileGridResult;
  /** True while the "Add Kong" form is open — signals a likely-forgotten "Add" tap. */
  hasUnsavedClaim(): boolean;
  /** Refreshes the seat/round badges in place (e.g. after the winner select changes) without touching any tiles already picked. */
  updateContext(context: TileGridContext): void;
}

export interface TileGridOptions {
  /**
   * Off for the photo-correction screen: a photo can't tell you which tile
   * actually won (it's just sitting in the hand, indistinguishable from the
   * rest), so asking for it there would invite a guess. It's picked for
   * real on the main entry form once the corrected hand lands there.
   */
  showWinningTilePicker?: boolean;
  /**
   * Called after the hand changes in any way — a tile, a kong, a flower, the winning tile, the claimed-set tick — so a screen can
   * recalculate its preview. Not called while the grid is first being drawn.
   */
  onChange?: () => void;
}

function kongLabel(tiles: Tile[]): string {
  const t = tiles[0]!;
  return `Kong ${HONOUR_LABEL[t] ? HONOUR_LABEL[t] : t}`;
}

export function renderTileGrid(
  container: HTMLElement,
  prefill?: TileGridPrefill,
  context?: TileGridContext,
  options?: TileGridOptions,
): TileGridHandle {
  const showWinningTilePicker = options?.showWinningTilePicker ?? true;
  let live = false; // set once the grid is fully built, so the first draw never calls back into a screen that is still being wired
  const notify = (): void => {
    if (live) options?.onChange?.();
  };
  let ctx = context;
  const counts = new Map<Tile, number>(ALL_TILES.map((t) => [t, 0]));

  // The hand as a fixed sequence of slots, not suit/rank and not grouped by
  // tile type — a player correcting a misread photo taps the wrong tile to
  // blank that exact slot, then taps the right one to fill it back in. If
  // removing a tile silently collapsed the rest leftward, the slot the
  // player is about to fix would already have moved by the time they tap
  // its replacement. `null` is an open slot, reserving its position until
  // something fills it (earliest-open-slot-first) or the slot itself is
  // tapped away entirely (see `renderPreview`).
  const handSlots: (Tile | null)[] = [];

  /** +1 copy of `tile`: takes the earliest open slot, or appends a new one. */
  function occupySlot(tile: Tile): void {
    const openIndex = handSlots.indexOf(null);
    if (openIndex !== -1) handSlots[openIndex] = tile;
    else handSlots.push(tile);
  }

  /** Blanks every slot currently holding `tile` (used when a grid tap wraps a count back to zero, or a kong claim shrinks another cap below what's held). Stops after `limit` slots if given, clearing the most recently added ones first. */
  function vacateSlotsOf(tile: Tile, limit = Infinity): void {
    let remaining = limit;
    for (let i = handSlots.length - 1; i >= 0 && remaining > 0; i--) {
      if (handSlots[i] === tile) {
        handSlots[i] = null;
        remaining--;
      }
    }
  }

  for (const t of prefill?.concealed ?? []) {
    counts.set(t, (counts.get(t) ?? 0) + 1);
    handSlots.push(t);
  }

  const kongs: DeclaredKong[] = (prefill?.melds ?? [])
    .filter((m) => m.type === 'kong')
    .map((m) => ({
      tiles: m.tiles,
      concealed: m.concealed,
      label: kongLabel(m.tiles),
      originLabel: m.concealed ? 'self-drawn' : 'claimed from discard',
    }));

  const bonusOn = new Set<BonusTile>(prefill?.flowers ?? []);
  let winningTile: Tile | undefined = prefill?.winningTile;
  let claimedSet = prefill?.claimedSet ?? false;

  container.innerHTML = `
    <div class="tile-grid-section">
      <div class="tile-grid-header">
        <span>Your hand</span>
      </div>
      <div class="tile-grid-preview" id="tgPreview"></div>
      <p class="muted" id="tgPreviewHint" style="font-size:0.75rem;margin-top:0.3rem"></p>
      <div id="tgHelper"></div>
    </div>

    <div class="tile-grid-section">
      <div class="tile-grid-header">
        <span>Concealed tiles</span>
        <span class="muted" id="tgConcealedCount"></span>
      </div>
      <div id="tgSuits"></div>
      <div class="tile-grid-label">Honours</div>
      <div class="tile-grid honours" id="tgHonours"></div>
      <div class="tile-grid-label">Flowers &amp; seasons</div>
      <div class="tile-grid bonus" id="tgBonus"></div>
    </div>

    <div class="tile-grid-section">
      <div class="tile-grid-header">
        <span>Kongs</span>
        <button type="button" class="secondary" id="tgAddClaim">+ Add Kong</button>
      </div>
      <div id="tgClaimedList"></div>
      <p class="muted" id="tgKongNote" role="status" style="display:none"></p>
      <div class="claim-form" id="tgClaimForm" style="display:none">
        <div class="claim-picker-row">
          <select id="tgSuitSel"></select>
          <select id="tgRankSel"></select>
        </div>
        <div class="claim-origin-row" id="tgOriginRow"></div>
        <input type="text" id="tgSourceInput" placeholder="Claimed from (optional), e.g. South" />
        <p class="muted" style="margin:0;font-size:0.8rem">
          Tiles of this kind already in your hand become part of the kong; the app adds the rest so it has 4.
        </p>
        <div class="row">
          <button type="button" id="tgClaimAdd">Add Kong</button>
          <button type="button" class="secondary" id="tgClaimCancel">Cancel</button>
        </div>
        <p class="error-box" id="tgClaimError" style="display:none"></p>
      </div>
      <label class="checkbox-row">
        <input type="checkbox" id="tgClaimedSet" ${claimedSet ? 'checked' : ''} />
        Claimed a pung or chow from a discard
      </label>
      <p class="muted">
        A claimed kong, an Added Kong (upgraded from a pung you claimed earlier), or checking the box
        above, ends the fully concealed hand bonus.
      </p>
    </div>

    ${
      showWinningTilePicker
        ? `<div class="tile-grid-label" id="tgWinningLabel">Winning tile — tap it below</div>
           <div class="tile-grid win-tile-picker" id="tgWinningTile"></div>`
        : ''
    }

    <div class="tile-grid-total" id="tgTotal"></div>
  `;

  const previewEl = container.querySelector<HTMLDivElement>('#tgPreview')!;
  const previewHintEl = container.querySelector<HTMLParagraphElement>('#tgPreviewHint')!;
  const suitsEl = container.querySelector<HTMLDivElement>('#tgSuits')!;
  const honoursEl = container.querySelector<HTMLDivElement>('#tgHonours')!;
  const bonusEl = container.querySelector<HTMLDivElement>('#tgBonus')!;
  const claimedListEl = container.querySelector<HTMLDivElement>('#tgClaimedList')!;
  const claimForm = container.querySelector<HTMLDivElement>('#tgClaimForm')!;
  const originRow = container.querySelector<HTMLDivElement>('#tgOriginRow')!;
  const suitSel = container.querySelector<HTMLSelectElement>('#tgSuitSel')!;
  const rankSel = container.querySelector<HTMLSelectElement>('#tgRankSel')!;
  const sourceInput = container.querySelector<HTMLInputElement>('#tgSourceInput')!;
  const claimError = container.querySelector<HTMLParagraphElement>('#tgClaimError')!;
  const kongNoteEl = container.querySelector<HTMLParagraphElement>('#tgKongNote')!;
  const winPickerEl = container.querySelector<HTMLDivElement>('#tgWinningTile');
  const totalEl = container.querySelector<HTMLDivElement>('#tgTotal')!;
  const concealedCountEl = container.querySelector<HTMLSpanElement>('#tgConcealedCount')!;
  const claimedSetCb = container.querySelector<HTMLInputElement>('#tgClaimedSet')!;
  claimedSetCb.addEventListener('change', () => {
    claimedSet = claimedSetCb.checked;
    notify();
  });

  function usedByClaims(tile: Tile): number {
    let n = 0;
    for (const k of kongs) for (const t of k.tiles) if (t === tile) n++;
    return n;
  }

  /**
   * A tile has 4 physical copies, full stop — the grid can hold all 4 with
   * no kong declared at all, e.g. 111122223333m decomposes into four
   * separate 123m chows, no kong involved. Kong declaration isn't about
   * "this tile hit 4"; it's about whether the player actually formed one
   * (which the decomposer can never infer from a flat concealed count).
   * The only hard limit here is the shared physical budget: whatever a
   * kong has already used shrinks how many copies the grid can still hold.
   */
  function gridCap(tile: Tile): number {
    return 4 - usedByClaims(tile);
  }

  const WIND_TILES = new Set<Tile>(['1z', '2z', '3z', '4z']);

  function makeTileButton(tile: Tile, label: string): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'tile-btn';
    btn.dataset.tile = tile;
    btn.dataset.label = label;
    const badges = WIND_TILES.has(tile)
      ? `<span class="tile-badge round" data-badge="round" style="display:none">R</span><span class="tile-badge seat" data-badge="seat" style="display:none">S</span>`
      : '';
    btn.innerHTML = `<img class="tile-img" src="${iconUrl(tile)}" alt="${esc(label)}" draggable="false" />${badges}<span class="count"></span>`;
    btn.addEventListener('click', () => {
      const cap = gridCap(tile);
      if (cap <= 0) return;
      const next = ((counts.get(tile) ?? 0) + 1) % (cap + 1);
      counts.set(tile, next);
      if (next === 0) vacateSlotsOf(tile);
      else occupySlot(tile);
      render();
    });
    return btn;
  }

  for (const { suit, label } of NUMBERED_SUITS) {
    const wrap = document.createElement('div');
    wrap.className = 'tile-grid-suit';
    const h = document.createElement('div');
    h.className = 'tile-grid-label';
    h.textContent = label;
    wrap.appendChild(h);
    const grid = document.createElement('div');
    grid.className = 'tile-grid';
    for (let r = 1; r <= 9; r++) grid.appendChild(makeTileButton(`${r}${suit}`, String(r)));
    wrap.appendChild(grid);
    suitsEl.appendChild(wrap);
  }

  for (const t of HONOUR_TILES) honoursEl.appendChild(makeTileButton(t, HONOUR_LABEL[t]!));

  for (const bt of BONUS_TILES) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'tile-btn bonus-btn';
    btn.innerHTML = `<img class="tile-img" src="${iconUrl(bt)}" alt="${bt}" draggable="false" /><span class="tile-badge seat" data-badge="seat" style="display:none">S</span><span class="count"></span>`;
    btn.dataset.bonus = bt;
    // The border-colour-only highlight this used to rely on reads clearly
    // in a high-contrast theme like Midnight Ink but washes out in lighter
    // ones — the same solid badge a regular tile shows for its count (✓
    // here, since a flower is on/off rather than counted) gives a
    // theme-independent, unmissable "this one's selected" signal.
    const syncSelected = () => {
      btn.classList.toggle('bonus-on', bonusOn.has(bt));
      btn.querySelector('.count')!.textContent = bonusOn.has(bt) ? '✓' : '';
    };
    syncSelected();
    btn.addEventListener('click', () => {
      if (bonusOn.has(bt)) bonusOn.delete(bt);
      else bonusOn.add(bt);
      syncSelected();
      notify();
    });
    bonusEl.appendChild(btn);
  }

  // --- kong picker: origin (self-drawn / claimed / added) + tile ---
  // "Added" is an Added Kong (加杠) — the 4th tile was drawn by the player,
  // but the pung it upgrades was itself claimed earlier from a discard, so
  // the kong is NOT concealed, same as an outright claimed kong. Modelled as
  // a third origin choice (not a separate checkbox layered on "Self-drawn")
  // specifically so the two can't be checked at once and contradict each
  // other — only one origin can ever be true for a given kong.
  type KongOrigin = 'self' | 'claimed' | 'added';
  let kongOrigin: KongOrigin = 'claimed';

  const ORIGIN_LABELS: Record<KongOrigin, string> = {
    self: 'Self-drawn',
    claimed: 'Claimed',
    added: 'Added Kong',
  };

  for (const o of ['self', 'claimed', 'added'] as const) {
    const b = document.createElement('button');
    b.type = 'button';
    b.textContent = ORIGIN_LABELS[o];
    b.dataset.origin = o;
    b.addEventListener('click', () => {
      kongOrigin = o;
      refreshOriginUI();
    });
    originRow.appendChild(b);
  }

  /** Updates origin toggle + source-field visibility only — never touches
   *  the suit/rank selects, so flipping self-drawn/claimed doesn't silently
   *  reset which tile was chosen. */
  function refreshOriginUI(): void {
    for (const b of originRow.querySelectorAll<HTMLButtonElement>('button')) {
      b.classList.toggle('active', b.dataset.origin === kongOrigin);
    }
    sourceInput.style.display = kongOrigin === 'self' ? 'none' : '';
  }

  function resetPickerForm(): void {
    refreshOriginUI();
    suitSel.innerHTML = '';
    const suitOpts = [...NUMBERED_SUITS.map(({ suit, label }) => ({ value: suit, label })), { value: 'z', label: 'Honour' }];
    for (const { value, label } of suitOpts) suitSel.appendChild(new Option(label, value));
    refreshRankOptions();
  }
  suitSel.addEventListener('change', refreshRankOptions);

  function refreshRankOptions(): void {
    rankSel.innerHTML = '';
    if (suitSel.value === 'z') {
      for (const t of HONOUR_TILES) rankSel.appendChild(new Option(HONOUR_LABEL[t]!, t[0]!));
    } else {
      for (let r = 1; r <= 9; r++) rankSel.appendChild(new Option(String(r), String(r)));
    }
  }

  container.querySelector<HTMLButtonElement>('#tgAddClaim')!.addEventListener('click', () => {
    const showing = claimForm.style.display !== 'none';
    claimForm.style.display = showing ? 'none' : 'flex';
    claimError.style.display = 'none';
    if (!showing) resetPickerForm();
  });
  container.querySelector<HTMLButtonElement>('#tgClaimCancel')!.addEventListener('click', () => {
    claimForm.style.display = 'none';
  });

  function tilesForKong(): { tiles: Tile[]; label: string } | undefined {
    const suit = suitSel.value;
    const rank = rankSel.value;
    if (!suit || !rank) return undefined;
    const t = suit === 'z' ? (`${rank}z` as Tile) : (`${rank}${suit}` as Tile);
    return { tiles: Array<Tile>(4).fill(t), label: kongLabel([t]) };
  }

  /** Takes every loose copy of `tile` out of the hand (the kong is about to own them), without leaving blank slots behind. */
  function takeOverLooseCopies(tile: Tile): void {
    for (let i = handSlots.length - 1; i >= 0; i--) {
      if (handSlots[i] === tile) handSlots.splice(i, 1);
    }
    counts.set(tile, 0);
  }

  container.querySelector<HTMLButtonElement>('#tgClaimAdd')!.addEventListener('click', () => {
    const result = tilesForKong();
    if (!result) {
      claimError.textContent = 'Pick a tile first.';
      claimError.style.display = 'block';
      return;
    }
    const kongTile = result.tiles[0]!;
    if (usedByClaims(kongTile) > 0) {
      // A second kong of the same tile cannot exist (there are only four copies) — say so instead of adding eight.
      claimError.textContent = `${result.label} is already declared.`;
      claimError.style.display = 'block';
      return;
    }
    claimError.style.display = 'none';
    // No "are there enough copies" check: look at what the hand already holds of this tile and make up the
    // difference — none -> 4 new, one -> it joins the kong + 3 new, and so on (see kongPlan).
    const plan = kongPlan(counts.get(kongTile) ?? 0);
    if (plan.absorb > 0) takeOverLooseCopies(kongTile);
    // Only a genuinely self-drawn kong (all 4 tiles the player's own, no
    // claim anywhere in its history) is concealed — an Added Kong upgrades a
    // pung that was itself claimed, so it scores as claimed too, even though
    // the 4th tile was drawn.
    const isSelfDrawn = kongOrigin === 'self';
    const source = sourceInput.value.trim();
    const originLabel =
      kongOrigin === 'self'
        ? 'self-drawn'
        : kongOrigin === 'added'
          ? `added from a pung claimed from ${source || 'discard'}`
          : `claimed from ${source || 'discard'}`;
    kongs.push({
      tiles: result.tiles,
      concealed: isSelfDrawn,
      label: result.label,
      originLabel,
    });
    sourceInput.value = '';
    claimForm.style.display = 'none';
    kongNoteEl.textContent = kongNote(result.label, plan);
    kongNoteEl.style.display = '';
    render();
  });

  function renderClaimed(): void {
    claimedListEl.innerHTML = '';
    if (kongs.length === 0) {
      const p = document.createElement('p');
      p.className = 'muted';
      p.textContent = 'None yet.';
      claimedListEl.appendChild(p);
      return;
    }
    kongs.forEach((k, i) => {
      const row = document.createElement('div');
      row.className = 'claimed-set-row';
      row.setAttribute('aria-label', `${k.label} — ${k.originLabel}`);
      const tilesHtml = k.tiles
        .map((t) => `<img class="hand-tile" src="${iconUrl(t)}" alt="${esc(t)}" draggable="false" />`)
        .join('');
      row.innerHTML = `
        <div class="claimed-set-info">
          <span class="claimed-set-type">Kong <span class="muted">— ${esc(k.originLabel)}</span></span>
          <div class="claimed-set-tiles">${tilesHtml}</div>
        </div>
      `;
      const x = document.createElement('button');
      x.type = 'button';
      x.className = 'remove-set';
      x.setAttribute('aria-label', 'Remove kong');
      x.textContent = '✕';
      x.addEventListener('click', () => {
        kongs.splice(i, 1);
        kongNoteEl.style.display = 'none';
        render();
      });
      row.appendChild(x);
      claimedListEl.appendChild(row);
    });
  }

  interface PreviewItem {
    tile: Tile | null;
    /** Index into `handSlots`, for a concealed slot (open or filled). */
    slotIndex: number | undefined;
    /** Index into `kongs`, for a kong tile. */
    kongIndex: number | undefined;
  }

  function previewItems(): PreviewItem[] {
    const items: PreviewItem[] = handSlots.map((tile, slotIndex) => ({
      tile,
      slotIndex,
      kongIndex: undefined,
    }));
    kongs.forEach((k, kongIndex) => {
      for (const tile of k.tiles) items.push({ tile, slotIndex: undefined, kongIndex });
    });
    return items;
  }

  /**
   * The whole hand at a glance, same tile faces the concealed grid and kong
   * list already use, laid out as fixed positions rather than grouped by
   * tile — so fixing a misread tile is a two-tap swap in place, not a
   * remove-then-hunt-for-where-it-landed. Tapping a filled slot blanks it
   * (an open square, reserving that exact position) instead of collapsing
   * the rest leftward; the next tile tapped in the grid below fills the
   * earliest open slot, not necessarily this one, if more than one is open.
   * Tapping an already-open slot removes it outright — for when a tile was
   * over-counted and there's no replacement coming. Kong tiles aren't part
   * of this — tapping one removes the whole kong immediately, same as
   * before, since a kong is a declared block, not an individual pick.
   */
  function renderPreview(): void {
    const items = previewItems();
    previewEl.innerHTML = '';
    if (items.length === 0) {
      const p = document.createElement('p');
      p.className = 'muted';
      p.textContent = 'Tap tiles below to build your hand.';
      previewEl.appendChild(p);
      previewHintEl.textContent = '';
      return;
    }
    previewHintEl.textContent = 'Tap a tile to remove it — tap the open slot again to delete it.';
    for (const item of items) {
      const btn = document.createElement('button');
      btn.type = 'button';

      if (item.tile === null) {
        btn.className = 'tile-btn preview-tile preview-blank';
        btn.setAttribute('aria-label', 'Open slot — tap to delete');
        btn.addEventListener('click', () => {
          handSlots.splice(item.slotIndex!, 1);
          render();
        });
        previewEl.appendChild(btn);
        continue;
      }

      btn.className = 'tile-btn preview-tile';
      btn.classList.toggle('is-kong', item.kongIndex !== undefined);
      btn.innerHTML = `<img class="tile-img" src="${iconUrl(item.tile)}" alt="${esc(item.tile)}" draggable="false" />`;
      btn.addEventListener('click', () => {
        if (item.kongIndex !== undefined) {
          kongs.splice(item.kongIndex, 1);
          kongNoteEl.style.display = 'none';
        } else {
          handSlots[item.slotIndex!] = null;
          counts.set(item.tile!, Math.max(0, (counts.get(item.tile!) ?? 0) - 1));
        }
        render();
      });
      previewEl.appendChild(btn);
    }
  }

  function render(): void {
    for (const btn of [...suitsEl.querySelectorAll<HTMLButtonElement>('.tile-btn'), ...honoursEl.querySelectorAll<HTMLButtonElement>('.tile-btn')]) {
      const tile = btn.dataset.tile!;
      const cap = gridCap(tile);
      const held = counts.get(tile) ?? 0;
      if (held > cap) {
        vacateSlotsOf(tile, held - Math.max(0, cap));
        counts.set(tile, Math.max(0, cap));
      }
      const n = counts.get(tile) ?? 0;
      const locked = cap <= 0;
      btn.disabled = locked;
      btn.classList.toggle('locked', locked);
      btn.classList.toggle('has-count', !locked && n > 0);
      btn.querySelector('.count')!.textContent = locked ? 'KONG' : n > 0 ? `×${n}` : '';
    }

    const concealedTotal = [...counts.values()].reduce((a, b) => a + b, 0);
    const kongTileTotal = kongs.reduce((a, k) => a + k.tiles.length, 0);
    const total = concealedTotal + kongTileTotal;
    const kongCount = kongs.length;
    const target = 14 + kongCount;

    concealedCountEl.textContent = `${concealedTotal} concealed`;

    renderWinningTilePicker();
    renderClaimed();
    renderPreview();

    if (total === target) {
      totalEl.textContent = `${total} / ${target} tiles${kongCount ? ` (${kongCount} kong${kongCount > 1 ? 's' : ''} included)` : ''} — ready to score.`;
      totalEl.classList.add('ready');
    } else {
      totalEl.textContent = `${total} / ${target} tiles (${concealedTotal} concealed + ${kongTileTotal} in kongs). ${
        total < target ? `Add ${target - total} more.` : `Remove ${total - target}.`
      }`;
      totalEl.classList.remove('ready');
    }
    notify();
  }

  /** Tap-to-select tile faces, built fresh each render from whatever's currently held. Skipped entirely when this screen doesn't ask for a winning tile. */
  function renderWinningTilePicker(): void {
    if (winningTile !== undefined && (counts.get(winningTile) ?? 0) === 0) {
      winningTile = undefined;
    }
    if (!winPickerEl) return;
    winPickerEl.innerHTML = '';
    const held = [...counts.entries()].filter(([, n]) => n > 0);
    if (held.length === 0) {
      const p = document.createElement('p');
      p.className = 'muted';
      p.textContent = 'Tap tiles above first.';
      winPickerEl.appendChild(p);
      return;
    }
    for (const [tile] of held) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'tile-btn win-tile-option';
      const isSelected = tile === winningTile;
      btn.classList.toggle('has-count', isSelected);
      // Same "✓" badge the flower/season tiles use for their own on/off
      // selection — a border-color change alone was too easy to miss at a
      // glance, especially with several tiles of the same suit in a row.
      btn.innerHTML = `<img class="tile-img" src="${iconUrl(tile)}" alt="${esc(tile)}" draggable="false" /><span class="count">${isSelected ? '✓' : ''}</span>`;
      btn.addEventListener('click', () => {
        winningTile = tile;
        renderWinningTilePicker();
        notify();
      });
      winPickerEl.appendChild(btn);
    }
  }

  /**
   * Shows which wind tiles match the winner's seat/the round's prevailing
   * wind, and which flower/season number matches the winner's own seat — the
   * same "R"/"S" badge idea TileBuddy uses. Only shown per-badge when the
   * corresponding house rule toggle is actually on, so this never implies a
   * bonus that isn't really active at this table.
   */
  function renderBadges(): void {
    for (const t of WIND_TILES) {
      const btn = honoursEl.querySelector<HTMLButtonElement>(`[data-tile="${t}"]`);
      const roundBadge = btn?.querySelector<HTMLElement>('[data-badge="round"]');
      const seatBadge = btn?.querySelector<HTMLElement>('[data-badge="seat"]');
      if (roundBadge) {
        roundBadge.style.display = ctx?.showPrevailingWindBadge && t === WIND_TILE[ctx.prevailingWind] ? '' : 'none';
      }
      if (seatBadge) {
        seatBadge.style.display = ctx?.showSeatWindBadge && t === WIND_TILE[ctx.seatWind] ? '' : 'none';
      }
    }
    for (const bt of BONUS_TILES) {
      const btn = bonusEl.querySelector<HTMLButtonElement>(`[data-bonus="${bt}"]`);
      const seatBadge = btn?.querySelector<HTMLElement>('[data-badge="seat"]');
      const w = bonusWindOf(bt);
      if (seatBadge) {
        seatBadge.style.display = ctx?.showSeatFlowerBadge && w === ctx.seatWind ? '' : 'none';
      }
    }
  }

  render();
  renderBadges();
  live = true;

  return {
    read(): TileGridResult {
      // handSlots, not counts' fixed suit/rank iteration order — the whole
      // point of tracking it is so the tiles actually submitted (and later
      // stored, scored, and printed) come out in the order they were
      // entered, not resorted underneath the player. Any still-open slots
      // (a correction left unfinished) are simply skipped — the resulting
      // short hand fails scoring's own tile-count check same as it always
      // has, rather than needing a separate gate here.
      const concealed: Tile[] = handSlots.filter((t): t is Tile => t !== null);
      const melds: Meld[] = kongs.map((k) => ({ type: 'kong', tiles: k.tiles, concealed: k.concealed }));
      return { concealed, melds, winningTile, flowers: [...bonusOn], claimedSet };
    },
    hasUnsavedClaim(): boolean {
      return claimForm.style.display !== 'none';
    },
    updateContext(context: TileGridContext): void {
      ctx = context;
      renderBadges();
    },
  };
}
